import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { Button } from '~components';
import { Row } from '~components/table-view-v3';

import { InvoiceStatusTransitions } from '~lookups/models';

import { updateStatus } from '~support-at-home/actions/supplierInvoices';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  row: Row;
  getRequiresSeniorApproval: (row: Row) => boolean;
}

type StatusAction = {
  to_status: string;
  label: string;
  requestedTag: string;
};

const statusActions: StatusAction[] = [
  { to_status: 'approved-unpaid', label: 'Approve', requestedTag: 'next' },
  { to_status: 'approved-paid', label: 'Mark Paid', requestedTag: 'next' },
  { to_status: 'declined', label: 'Decline', requestedTag: 'declined' },
];

const STATUS_APPROVED_UNPAID = 'approved-unpaid';

const ActionButtons: React.FC<ComponentProps> = ({ row, getRequiresSeniorApproval }) => {
  const SENIORAPPROVALTAG = AuthTags.SUPPORTATHOME.SUPPLIERINVOICES.SENIOR;
  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState(false);

  const { allowedTransitions, status, id: invoiceId } = row || {};
  const requiresSeniorApproval = getRequiresSeniorApproval?.(row) ?? row.requires_senior_approval ?? false;
  const tags = useAppSelector(selectUserTags);

  if (!status?.tag) return null;

  const actions = statusActions.filter((action) => {
    if (action.to_status === STATUS_APPROVED_UNPAID && requiresSeniorApproval && !tags.includes(SENIORAPPROVALTAG)) {
      return false;
    }
    return allowedTransitions?.some((t: InvoiceStatusTransitions) => t.to_status?.tag === action.to_status);
  });

  if (!actions) {
    return <span>No action required</span>;
  }

  const leftAction = actions.find((a) => a.requestedTag === 'next');
  const rightActions = actions.filter((a) => a.requestedTag !== 'next');

  const hasButtons = leftAction || rightActions.length > 0;

  if (!hasButtons) {
    return <span>No action required</span>;
  }

  const handleStatusChange = (tag: string) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      dispatch(updateStatus({ id: invoiceId, new_status: tag }));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="d-flex gap-3 align-items-start">
      {leftAction && (
        <div className="d-inline-block" id={`tooltip-${row.id}-${leftAction.requestedTag}`}>
          <Button
            id={`btn-${row.id}-${leftAction.requestedTag}`}
            size="sm"
            color="success"
            style={{ width: '100px' }}
            onClick={() => handleStatusChange(leftAction.to_status)}
            disabled={isUpdating}
          >
            {leftAction.label}
          </Button>
        </div>
      )}

      {rightActions.length > 0 && (
        <div className="d-flex gap-3">
          {rightActions.map((action) => {
            return (
              <div key={action.requestedTag} className="d-inline-block" id={`tooltip-${row.id}-${action.requestedTag}`}>
                <Button
                  id={`btn-${row.id}-${action.requestedTag}`}
                  size="sm"
                  color="warning"
                  style={{ width: '100px' }}
                  onClick={() => handleStatusChange(action.to_status)}
                  disabled={isUpdating}
                >
                  {action.label}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionButtons;
